// React
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Services
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { ContactNoteRow } from "services/api/contacts/types";
import { deleteContactNoteAPI, getContactNotesAPI } from "services/api/contacts/api";

// Types
import { ContactNoteCardProps, ContactNoteRef } from "./types";

// Components
import { Tabs } from "ui/components/Tabs";
import ContactNoteEditCard from "../ContactNoteEditCard/ContactNoteEditCard";
import ContactNoteSearchTab from "./components/ContactNoteSearchTab/ContactNoteSearchTab";
import ContactNoteTab from "./components/ContactNoteTab/ContactNoteTab";
import { Dialog } from "ui/components/Dialog";

const ContactNoteCard = forwardRef<ContactNoteRef, ContactNoteCardProps>((props, ref) => {
  const { contactDetail } = props;

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ContactNoteRow>
  >(initialDataWithPagination);

  const [noteId, setNoteId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (contactDetail.id)
      fetchSearchResult(searchResults.pagination, contactDetail.id);
  }, [contactDetail.id]);

  const fetchSearchResult = async (pagination: Pagination, contactId: string) => {
    setIsLoading(true);
    try {
      const results = await getContactNotesAPI(pagination, contactId);
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    if (id) {
      setNoteId(id);
      setShowAddNewModal(true);
    }
  }, []);

  const openAddNew = () => {
    setNoteId("");
    setShowAddNewModal(true);
  };

  const handleEditSuccess = useCallback(() => {
    if (contactDetail.id)
      fetchSearchResult(searchResults.pagination, contactDetail.id);
    setShowAddNewModal(false);
  }, [contactDetail.id, searchResults.pagination]);

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setNoteId(id);
    setOpenDialog(true);
  };

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!noteId) return;

    try {
      if (contactDetail.id) {
        const deleted = await deleteContactNoteAPI(
          t,
          contactDetail.id,
          noteId,
        );

        if (deleted) {
          await fetchSearchResult(searchResults.pagination, contactDetail.id);
        }
      }

      setOpenDialog(false);
      setNoteId("");
    } catch (err) {
      console.error("Greška prilikom brisanja bilješke:", err);
    }
  }, [noteId, t, contactDetail.id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setNoteId("");
    setOpenDialog(false);
  }, []);

  useImperativeHandle(ref, () => ({
    openAddNew,
  }));

  if (!contactDetail.id) return <></>;

  return (
    <>
      <Tabs
        tabs={[
          {
            label: t("admin.contact.note.tab.visual"),
            content: (
              <div>
                <ContactNoteTab
                  searchResults={searchResults}
                  isLoading={isLoading}
                  onRowClick={handleRowClick}
                  onDeleteClick={handleDeleteClick}
                />
              </div>
            ),
          },
          {
            label: t("admin.contact.note.tab.search"),
            content: (
              <div>
                <ContactNoteSearchTab
                  searchResults={searchResults}
                  isLoading={isLoading}
                  onRowClick={handleRowClick}
                  onDeleteClick={handleDeleteClick}
                />
              </div>
            ),
          },
        ]}
      />

      <ContactNoteEditCard
        noteId={noteId}
        contactId={contactDetail.id}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t("admin.contact.note.dialog.delete.title")}
        message={t("admin.contact.note.dialog.delete.message")}
        confirmText={t("admin.contact.note.dialog.delete.confirmtext")}
        cancelText={t("admin.contact.note.dialog.delete.canceltext")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
});

export default memo(ContactNoteCard);
